import React, { useEffect, useState } from 'react';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { IoChevronBackOutline } from 'react-icons/io5';
import Button from '../../../Component/Button/Button';
import DropDown from '../../../Component/DropDown/DropDown';
import Input from '../../../Component/Input/Input';
import SideBarSkeleton from '../../../Component/SideBarSkeleton';
import classes from './NewEvent.module.css';

import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { MdAutoAwesome } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import validator from 'validator';
import { Get } from '../../../Axios/AxiosFunctions';
import CustomFileInput from '../../../Component/CustomFileInput';
import FileUpload from '../../../Component/FileUpload';
import FormLanguages from '../../../Component/FormLanguages';
import { Loader } from '../../../Component/Loader';
import LocationComponent from '../../../Component/LocationComponent';
import LottieLoader from '../../../Component/LottieLoader/LottieLoader';
import QuillInput from '../../../Component/QuillInput';
import UploadImageBox from '../../../Component/UploadImageBox';
import { BaseURL, CreateFormData, formatLabel } from '../../../config/apiUrl';
import {
  defaultLocation,
  languagesKeysObject,
  languagesOptions
} from '../../../constant/commonData';
import {
  useAddOrEditEventMutation,
  useDeleteEventMediaMutation,
  useGetSingleEventQuery
} from '../../../store/apiSlice/apiSlice';
import { uploadImagesHelper } from '../../../utils/helpers';

function NewEvent() {
  const navigate = useNavigate();
  const slug = useParams()?.slug;
  // Language
  const [language, setLanguage] = useState(languagesOptions[0]);
  // Language

  const { t } = useTranslation(['dashboardEvents', 'locationComp'], {
    lng: language?.value
  });
  const { t: languageD } = useTranslation('locationComp', { lng: language?.value });
  const [deleteEventMedia, { isLoading: isDeletingMedia }] = useDeleteEventMediaMutation();

  // new image states
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [autoFillLoading, setAutoFillLoading] = useState(false);

  // Language
  const [formStates, setFormStates] = useState({
    eventName: languagesKeysObject,
    title: languagesKeysObject,
    description: languagesKeysObject,
    eventType: '',
    price: '',
    DateTime: '',
    pdf: null
  });

  //  media
  const [images] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [videoLink, setVideoLink] = useState('');
  const { eventTypes } = useSelector((state) => state?.commonReducer);
  const [addOrEditFn, { isLoading: loading }] = useAddOrEditEventMutation();

  const { data: singleEventData, isLoading: loaderApi } = useGetSingleEventQuery(
    { slug: slug },
    {
      skip: !slug
    }
  );

  const [locationState, setLocationState] = useState({
    province: '',
    town: '',
    neighbourhood: '',
    address: '',
    lat: '',
    lng: '',
    ...defaultLocation
  });

  const accessToken = useSelector((state) => state?.authReducer?.accessToken);
  // check if eligible for adding new property
  useEffect(() => {
    if (!slug) {
      const handleEligibility = async () => {
        const url = BaseURL(`users/check-limit?type=event`);
        const res = await Get(url, accessToken);
        if (res) {
          if (res?.data?.data === false) {
            toast.error(
              'You have reached the limit of adding new events, please upgrade your plan'
            );
            navigate(-1);
          }
        }
      };

      handleEligibility();
    }
  }, []);

  const validateMultiLanguageFields = (multiLanguageParams) => {
    for (let lang in languagesKeysObject) {
      const langLabel = languagesOptions.find((e) => e?.value === lang)?.label;
      for (let key in multiLanguageParams) {
        if (!multiLanguageParams[key][lang]) {
          toast.error(`Please fill the ${formatLabel(key)} field in language "${langLabel}".`);
          return false;
        }
      }
    }
    return true;
  };

  const validateUrlField = (key, value) => {
    if (!key.includes('Link') && !key.includes('link')) {
      return true;
    }
    if (typeof value === 'object') {
      return true;
    }
    if (!validator.isURL(value)) {
      toast.error(`Please enter a valid ${formatLabel(key)}.`);
      return false;
    }
    return true;
  };

  const validateRequiredFields = (params) => {
    for (let key in params) {
      if (['pdf'].includes(key)) continue;
      if (!params[key]) {
        toast.error(`Please fill the ${formatLabel(key)} field.`);
        return false;
      }
      if (!validateUrlField(key, params[key])) {
        return false;
      }
    }
    return true;
  };

  const validateImages = () => {
    if (uploadedImages?.length + images?.length < 1) {
      toast.error('Please upload at least 1 event images.');
      return false;
    }
    return true;
  };

  const validateParams = () => {
    const multiLanguageParams = {
      name: formStates?.eventName,
      title: formStates?.title,
      description: formStates?.description
    };

    const params = {
      eventType: formStates?.eventType?._id,
      price: formStates?.price,
      eventDate: formStates?.DateTime,
      images: uploadedImages,
      videoLink,
      province: locationState?.province?._id,
      town: locationState?.town?._id,
      neighborhood: locationState?.neighbourhood?._id,
      address: locationState?.address,
      lat: locationState?.lat,
      lng: locationState?.lng,
      pdf: formStates?.pdf
    };

    if (!validateMultiLanguageFields(multiLanguageParams)) {
      return [false, null];
    }

    if (!validateRequiredFields(params)) {
      return [false, null];
    }

    if (!validateImages()) {
      return [false, null];
    }

    return [true, { ...params, ...multiLanguageParams }];
  };

  const handleSubmit = async () => {
    const [isValid, params] = validateParams();
    if (!isValid) return;
    const formData = CreateFormData(params);
    const response = await addOrEditFn({ data: formData, slug });
    if (response.data) {
      toast.success(`Event ${slug ? 'updated' : 'created'} successfully`);
      navigate(-1);
    }
  };

  useEffect(() => {
    if (singleEventData?.data) {
      setFormStates({
        eventName: singleEventData?.data?.name,
        title: singleEventData?.data?.title,
        description: singleEventData?.data?.description,
        eventType: singleEventData?.data?.eventType,
        price: singleEventData?.data?.price?.toString(),
        DateTime: moment(singleEventData?.data?.eventDate).format('YYYY-MM-DD')
      });
      setLocationState({
        province: singleEventData?.data?.province,
        town: singleEventData?.data?.town,
        neighbourhood: singleEventData?.data?.neighborhood,
        address: singleEventData?.data?.address,
        lat: singleEventData?.data?.location.coordinates[1],
        lng: singleEventData?.data?.location.coordinates[0]
      });
      setVideoLink(singleEventData?.data?.videoLink);
      setUploadedImages(singleEventData?.data?.images);
    }
  }, [singleEventData]);

  const deleteMedia = async (e) => {
    const res = await deleteEventMedia(e);
    if (res?.data) {
      if (e?.type == 'images') {
        setUploadedImages((prev) => prev.filter((a) => a !== e?.key));
      }
      toast.success('Media deleted successfully');
    }
  };

  const handleAutoFill = async () => {
    setAutoFillLoading(true);
    const multiLanguageFields = {
      eventName: formStates?.eventName,
      title: formStates?.title,
      description: formStates?.description
    };

    const currentLang = language?.value;
    const langLabel = languagesOptions?.find((e) => e?.value === currentLang)?.label ?? 'Unknown';

    for (const key in multiLanguageFields) {
      const value = multiLanguageFields[key]?.[currentLang]?.trim();
      const label = formatLabel(key);
      if (!value) {
        let missingName = '';
        if (label === 'Event Name') {
          missingName = 'Event Name';
        } else if (label === 'Title') {
          missingName = 'Event Title';
        } else {
          missingName = label;
        }
        toast.error(`Missing field "${missingName}" in ${langLabel}`);
        setAutoFillLoading(false);
        return;
      }
    }

    // Show loading toast
    const loadingToast = toast.loading('Translating fields to other languages...');

    try {
      const response = await fetch(
        'https://turegu-ai-api.devcustomprojects.online/translate/property',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...multiLanguageFields
          }),
          redirect: 'follow'
        }
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      setFormStates((prevState) => ({
        ...prevState,
        eventName: {
          ...prevState.eventName,
          ...data?.eventName
        },
        title: {
          ...prevState.title,
          ...data?.title
        },
        description: {
          ...prevState.description,
          ...data?.description
        }
      }));

      toast.success(`Fields translated successfully for all languages!`);
    } catch (error) {
      console.log(error.message);
      toast.warning('Translation service unavailable.');
    } finally {
      toast.dismiss(loadingToast);
      setAutoFillLoading(false);
    }
  };

  const getLoadingToast = (loading, singleEventData) => {
    if (loading) {
      return t('form.loadingButton');
    }
    if (singleEventData) {
      return t('form.updateButton');
    }
    return t('form.submitButton');
  };

  return (
    <SideBarSkeleton>
      {loaderApi ? (
        <Loader className={classes?.loader} />
      ) : (
        <Container fluid className={classes.page}>
          <Row>
            <Col md={12} className={classes.mb30}>
              <Button
                leftIcon={<IoChevronBackOutline />}
                variant=""
                className={classes.backBtn}
                onClick={() => {
                  navigate(-1);
                }}>
                {t('form.goBack')}
              </Button>
            </Col>
            <Col md={12}>
              <div
                className={[
                  classes.formBg,
                  ['ar', 'fa']?.includes(language?.value) ? 'rtl' : 'ltr'
                ].join(' ')}>
                <Container>
                  <Row className={classes.formRow}>
                    <Col md={12} className="d-flex justify-content-between align-items-center">
                      <h3 className={classes.title}>
                        {slug ? t('form.titleEdit') : t('form.titleCreate')}{' '}
                      </h3>

                      <Button
                        variant="secondary"
                        onClick={handleAutoFill}
                        disabled={autoFillLoading}
                        className="d-flex align-items-center gap-2"
                        customStyle={{
                          width: 'fit-content'
                        }}>
                        {autoFillLoading ? (
                          <Spinner size="sm" className="me-2" />
                        ) : (
                          <MdAutoAwesome size={20} className="me-2" />
                        )}
                        {t('form.autoFill')}
                      </Button>
                    </Col>

                    <Col md={12} className="languageBar">
                      <div className={classes.headAndDropDown}>
                        <h4>{t('form.tagline')}</h4>
                        <FormLanguages
                          value={language}
                          setter={setLanguage}
                          className={classes.languagesMain}
                        />
                      </div>
                    </Col>

                    <Col md={12}>
                      <Input
                        label={t('form.eventsTitleLabel')}
                        placeholder={t('form.eventsTitlePlaceholder')}
                        variant="dashboard"
                        value={formStates?.eventName?.[language?.value]}
                        setValue={(e) => {
                          setFormStates({
                            ...formStates,
                            eventName: {
                              ...formStates?.eventName,
                              [language?.value]: e
                            }
                          });
                        }}
                        length={20}
                      />
                    </Col>
                    <Col md={12}>
                      <Input
                        label={t('form.eventsTitle')}
                        placeholder={t('form.eventsPlaceholder')}
                        variant="dashboard"
                        value={formStates?.title?.[language?.value]}
                        setValue={(e) => {
                          setFormStates({
                            ...formStates,
                            title: {
                              ...formStates?.title,
                              [language?.value]: e
                            }
                          });
                        }}
                        length={70}
                      />
                    </Col>
                    <Col md={12}>
                      <QuillInput
                        key={`description-${language?.value}`}
                        label={t('form.eventsDescriptionLabel')}
                        placeholder={t('form.eventsDescriptionPlaceholder')}
                        variant="dashboard"
                        value={formStates?.description?.[language?.value]}
                        setter={(e) => {
                          if (autoFillLoading) {
                            return;
                          }
                          setFormStates({
                            ...formStates,
                            description: {
                              ...formStates?.description,
                              [language?.value]: e
                            }
                          });
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <DropDown
                        label={t('form.eventsTypeLabel')}
                        options={eventTypes}
                        indicatorColor="var(--dashboard-text-gray-clr)"
                        placeholder={t('form.eventsTypePlaceholder')}
                        variant="dashboard"
                        optionValue={'_id'}
                        getOptionLabel={(e) => {
                          return e?.title[language?.value];
                        }}
                        value={formStates?.eventType}
                        setValue={(e) => {
                          setFormStates({ ...formStates, eventType: e });
                        }}
                      />
                    </Col>

                    <Col md={6}>
                      <Input
                        type={'date'}
                        label={t('form.eventsDateLabel')}
                        variant="dashboard"
                        value={formStates?.DateTime}
                        setValue={(e) => {
                          setFormStates({ ...formStates, DateTime: e });
                        }}
                        min={moment().format('YYYY-MM-DD')}
                      />
                    </Col>
                    <Col md={6}>
                      <Input
                        type={'number'}
                        label={t('form.eventsPrice')}
                        placeholder={t('form.eventsPricePlaceholder')}
                        variant="dashboard"
                        value={formStates?.price}
                        setValue={(e) => {
                          setFormStates({ ...formStates, price: e });
                        }}
                        min={0}
                        zeroAllowed
                      />
                    </Col>

                    {/* Location */}

                    <LocationComponent
                      selectedLanguage={language}
                      t={languageD}
                      locationState={locationState}
                      setLocationState={setLocationState}
                    />

                    {/* media */}
                    <Col md={12}>
                      <h4>{t('form.eventsMediaText')}</h4>
                    </Col>

                    <Col md={12}>
                      <label className={classes.label}>{t('form.eventsImagesText')}</label>
                      <Row className="gy-3">
                        {uploadedImages?.map((item, index) => (
                          <Col md={3} key={`${item}${index.toString()}`}>
                            <UploadImageBox
                              value={item}
                              edit={false}
                              onDelete={() => {
                                deleteMedia({ slug, type: 'images', key: item });
                              }}
                            />
                          </Col>
                        ))}
                      </Row>
                    </Col>

                    <Col md={12}>
                      <FileUpload
                        orText={t('form.orText')}
                        text={t('form.dragAndDrop')}
                        buttonLabel={t('form.browseFiles')}
                        files={images}
                        setFiles={(e) => {
                          uploadImagesHelper({
                            images: e,
                            setIsLoading: setIsUploadingImages,
                            setImages: setUploadedImages,
                            token: accessToken
                          });
                        }}
                      />
                    </Col>

                    <Col md={12}>
                      <CustomFileInput
                        label={t('form.projectPdfCatalogue')}
                        placeholder={''}
                        value={formStates?.pdf}
                        isEdit={singleEventData?.data?.pdfName}
                        setValue={(e) => {
                          if (!['application/pdf'].includes(e?.type)) {
                            toast.error(t('toast.pdfOnly'));
                            return;
                          }
                          setFormStates({ ...formStates, pdf: e });
                        }}
                        selectedLanguage={language}
                        btnLabel={t('form.projectPdfCatalogueBtn')}
                      />
                    </Col>

                    <Col md={12}>
                      <Input
                        label={t('form.eventsVideoLabel')}
                        placeholder={t('form.eventsVideoPlaceholder')}
                        variant="dashboard"
                        value={videoLink}
                        setValue={setVideoLink}
                      />
                    </Col>

                    <Col className="text-center">
                      <Button
                        disabled={loading}
                        onClick={handleSubmit}
                        label={getLoadingToast(loading, singleEventData)}
                      />
                    </Col>
                  </Row>
                </Container>
              </div>
            </Col>
          </Row>
        </Container>
      )}
      {(isUploadingImages || isDeletingMedia) && <LottieLoader />}
    </SideBarSkeleton>
  );
}

export default NewEvent;
